import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail, uploadFileUrl } from "@/lib/api";
import { UserPlus, Camera, Loader2, ArrowRight, Sparkles, CheckCircle2, Globe, MessageCircle, X } from "lucide-react";

const REMEMBERED_KEY = "hassein.remembered.email";

export default function Register() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [gender, setGender] = useState("male");
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  const onPhotoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload-public", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Store just the filename (uploadFileUrl adds /api/uploads/ prefix)
      setPhotoUrl(res.data.filename);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "העלאת תמונה נכשלה");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!photoUrl) {
      setErr("תמונת פרופיל היא שדה חובה");
      return;
    }
    // Show pricing popup BEFORE creating the account
    setShowPricing(true);
  };

  const confirmAndRegister = async () => {
    setShowPricing(false);
    setBusy(true);
    try {
      const res = await api.post("/auth/register", {
        email,
        password,
        full_name: fullName,
        gender,
        profile_image: photoUrl,
      });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem(REMEMBERED_KEY, email);
      setSession?.(res.data);
      navigate("/app");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "הרשמה נכשלה");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background:
          "radial-gradient(1200px 600px at 90% -10%, #E7F1EA 0%, transparent 60%), radial-gradient(900px 500px at -10% 100%, #FCE7D8 0%, transparent 55%), #F9FAF9",
      }}
    >
      <form onSubmit={submit} className="card-surface p-8 md:p-10 grain w-full max-w-md" data-testid="register-form">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: "var(--brand)", color: "#fff" }}>
            <UserPlus size={20} />
          </div>
          <div>
            <div className="font-heebo font-bold text-xl">הרשמה חדשה</div>
            <div className="text-sm text-slate-500">צור חשבון פרטי משלך</div>
          </div>
        </div>

        {/* Photo picker */}
        <div className="flex flex-col items-center mb-5">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg group"
            style={{
              background: photoUrl ? "transparent" : "linear-gradient(135deg,#EDE9FE,#DBEAFE)",
            }}
            data-testid="register-photo-btn"
          >
            {photoUrl ? (
              <img src={uploadFileUrl(photoUrl)} alt="פרופיל" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-500">
                {uploading ? <Loader2 size={22} className="animate-spin" /> : <Camera size={22} />}
              </div>
            )}
            <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] py-1 opacity-0 group-hover:opacity-100 transition">
              שנה תמונה
            </div>
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoPick} data-testid="register-photo-input" />
          <div className="text-[11px] text-slate-500 font-heebo mt-2">תמונה חובה · לחץ להעלאה</div>
        </div>

        <label className="block mb-3">
          <div className="text-xs text-slate-500 mb-1 font-heebo">שם מלא</div>
          <input
            type="text"
            className="input-field"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            data-testid="register-name-input"
          />
        </label>

        <label className="block mb-3">
          <div className="text-xs text-slate-500 mb-1 font-heebo">אימייל</div>
          <input
            type="email"
            className="input-field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="register-email-input"
            dir="ltr"
          />
        </label>

        <label className="block mb-3">
          <div className="text-xs text-slate-500 mb-1 font-heebo">סיסמה (4+ תווים)</div>
          <input
            type="text"
            className="input-field pw-masked"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={4}
            data-testid="register-password-input"
            dir="ltr"
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            name="pw-hassein-new"
            role="textbox"
            aria-label="סיסמה"
          />
        </label>

        <div className="block mb-5">
          <div className="text-xs text-slate-500 mb-2 font-heebo">מגדר (איך לפנות אליך)</div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: "male", label: "זכר" },
              { v: "female", label: "נקבה" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setGender(opt.v)}
                className={`p-2 rounded-xl border-2 text-sm font-heebo transition ${
                  gender === opt.v ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"
                }`}
                data-testid={`register-gender-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="mb-4 chip chip-danger" data-testid="register-error">{err}</div>}

        <button type="submit" disabled={busy || uploading || !photoUrl} className="btn-primary w-full justify-center" data-testid="register-submit-button">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
          {busy ? "נרשם…" : "צור חשבון"}
        </button>

        <div className="mt-6 text-center text-sm">
          <span className="text-slate-500 font-heebo">כבר יש חשבון? </span>
          <Link to="/login" className="font-heebo font-semibold" style={{ color: "var(--brand)" }} data-testid="link-to-login">
            כניסה <ArrowRight size={14} className="inline" />
          </Link>
        </div>
      </form>

      {showPricing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pricing-popup">
          <div className="card-surface p-6 md:p-8 max-w-md w-full grain relative animate-in fade-in zoom-in duration-200">
            <button
              type="button"
              onClick={() => setShowPricing(false)}
              className="absolute top-4 left-4 p-1 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
              aria-label="סגור"
              data-testid="pricing-popup-close"
            >
              <X size={18} />
            </button>
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3" style={{ background: "linear-gradient(135deg, #DCFCE7, #A7F3D0)", color: "#166534" }}>
                <Sparkles size={30} />
              </div>
              <div className="font-heebo font-black text-2xl text-slate-900">נסה חינם ל-30 יום</div>
            </div>

            <div className="rounded-xl p-4 border mb-4" style={{ background: "linear-gradient(135deg, #EFF6FF, #DBEAFE)", borderColor: "#93C5FD" }} data-testid="pricing-plan">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-xs font-heebo text-blue-800">לאחר תקופת הניסיון</div>
                <div>
                  <span className="num-pop text-3xl text-blue-900">129 ₪</span>
                  <span className="text-xs text-blue-700 font-heebo mr-1">/ חודש</span>
                </div>
              </div>
              <div className="text-[11px] text-blue-800 font-heebo mb-2 font-semibold">ללא התחייבות - ניתן לבטל מתי שרוצים</div>
              <ul className="space-y-2 mt-3 text-sm font-heebo text-blue-900">
                <li className="flex items-center gap-2">
                  <Globe size={14} /> דומיין אישי משלך
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} /> 500 MB אחסון קבצים
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle size={14} /> שירות לקוחות בוואטסאפ
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} /> ביטול בכל עת · בלי התחייבות
                </li>
              </ul>
            </div>

            <button
              type="button"
              className="btn-primary w-full justify-center text-lg py-3"
              onClick={confirmAndRegister}
              disabled={busy}
              data-testid="pricing-popup-confirm"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {busy ? "יוצר חשבון…" : "התחל את ה-30 יום החינם"}
            </button>
            <button
              type="button"
              onClick={() => setShowPricing(false)}
              className="mt-2 w-full text-xs text-slate-500 hover:text-slate-800 font-heebo py-2"
              data-testid="pricing-popup-cancel"
            >
              חזרה לטופס
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
